/**
 * Copy and derived shape for extraction progress rows.
 *
 * Kept separate from the component so the wording — which is the whole point of
 * a truthful progress surface — can be asserted directly, and so the row stays
 * a rendering concern.
 */
import type {
  ExtractionRun,
  ExtractionRunPhase,
} from '@/lib/extraction-runs/extraction-run-store';
import { isTerminalExtractionRunPhase } from '@/lib/extraction-runs/extraction-run-store';

const PHASE_LABELS: Record<ExtractionRunPhase, string> = {
  queued: 'Queued',
  running: 'Extracting',
  succeeded: 'Extraction complete',
  partial: 'Completed with exceptions',
  failed: 'Extraction failed',
  cancelled: 'Extraction cancelled',
};

export function extractionRunPhaseLabel(phase: ExtractionRunPhase): string {
  return PHASE_LABELS[phase];
}

function pluralize(count: number, singular: string): string {
  return `${String(count)} ${singular}${count === 1 ? '' : 's'}`;
}

/**
 * The document ledger, stated plainly. Every number is a whole document the
 * server classified — there is no "roughly done" and no per-document fraction,
 * because a document is only ever complete once all of its extraction units
 * have settled successfully.
 */
function describeExtractionRunCounts(run: ExtractionRun): string {
  const counts = run.counts;
  const segments = [
    `${String(counts.succeeded)} of ${pluralize(counts.total, 'document')} extracted`,
  ];
  if (counts.partial > 0) {
    segments.push(`${String(counts.partial)} partially extracted`);
  }
  if (counts.failed > 0) {
    segments.push(`${String(counts.failed)} failed`);
  }
  if (counts.cancelled > 0) {
    segments.push(`${String(counts.cancelled)} cancelled`);
  }
  const remaining = counts.queued + counts.running;
  if (remaining > 0 && run.sectionCount === 0) {
    segments.push(`${String(remaining)} remaining`);
  }
  return segments.join(' · ');
}

/** Use actual run units for matrices, retaining the document outcome ledger. */
export function describeExtractionRunProgress(run: ExtractionRun): string {
  const documents = describeExtractionRunCounts(run);
  if (run.sectionCount === 0) return documents;
  const counts = run.unitCounts;
  const segments = [
    `${String(counts.succeeded)} of ${pluralize(counts.total, 'section')} extracted`,
  ];
  if (counts.failed > 0) segments.push(`${String(counts.failed)} failed`);
  if (counts.cancelled > 0) segments.push(`${String(counts.cancelled)} cancelled`);
  if (counts.running > 0) segments.push(`${String(counts.running)} running`);
  if (counts.queued > 0) segments.push(`${String(counts.queued)} queued`);
  // One section per document: the document ledger would only repeat the numbers.
  if (counts.total === run.counts.total) return segments.join(' · ');
  return `${segments.join(' · ')} — ${documents}`;
}

export function extractionRunProgressCounts(run: ExtractionRun) {
  return run.sectionCount > 0 ? run.unitCounts : run.counts;
}

/** Known work starts at zero; only a missing total is indeterminate. */
export function isExtractionRunIndeterminate(run: ExtractionRun): boolean {
  return extractionRunProgressCounts(run).total === 0;
}

/** Shares of the work, 0–1, that the meter draws from its left edge. */
export interface ExtractionRunMeterSegments {
  /** Work that extracted cleanly. */
  readonly extracted: number;
  /** Work that settled, cleanly or not; the band past `extracted` is unresolved. */
  readonly settled: number;
}

export function extractionRunMeterSegments(
  counts: Pick<ExtractionRun['counts'], 'total' | 'succeeded' | 'settled'>,
): ExtractionRunMeterSegments {
  if (counts.total === 0) {
    return { extracted: 0, settled: 0 };
  }
  return {
    extracted: counts.succeeded / counts.total,
    settled: counts.settled / counts.total,
  };
}

/** Spoken form of the meter — carries the same truth without colour or motion. */
export function extractionRunValueText(run: ExtractionRun): string {
  if (extractionRunProgressCounts(run).total === 0) {
    return `${extractionRunPhaseLabel(run.phase)}. Work count not reported yet.`;
  }
  return `${describeExtractionRunProgress(run)}.`;
}

/** One calm sentence per state change, for assistive technology. */
export function extractionRunAnnouncement(run: ExtractionRun): string {
  const state = `${extractionRunPhaseLabel(run.phase)}: ${run.gridLabel}.`;
  const failure =
    run.failureMessage === null ? '' : ` Failure reported: ${run.failureMessage}`;
  // Only a settled extraction carries its tally into the announcement; while
  // work is in flight the sentence changes on phase transitions alone, so a
  // long extraction is not narrated document by document.
  return isTerminalExtractionRunPhase(run.phase)
    ? `${state} ${describeExtractionRunProgress(run)}${failure}`
    : state;
}
